import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Folder,
  FolderOpen,
  CheckCircle2,
  Circle,
  CircleDot,
  Lock,
} from 'lucide-react';
import { getCourse } from '../../services/coursesApi';
import type { CourseDetail, Lesson, Week } from '../../services/coursesApi';
import { useCourseStore } from '../../stores/courseStore';
import { useAuthStore } from '../../stores/authStore';
import { useUiStore } from '../../stores/uiStore';
import { weekHasLessons, weekLessonProgress } from '../course/weekPresentation';
import Spinner from '../ui/Spinner';

interface LeftNavProps {
  // On mobile, navigating should close the drawer.
  onNavigate?: () => void;
}

// Completion glyph per lesson status — the "mail unread/read" affordance.
function StatusDot({ status }: { status?: Lesson['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={15} className="text-ws-success shrink-0" />;
    case 'in_progress':
      return <CircleDot size={15} className="text-ws-accent shrink-0" />;
    case 'locked':
      return <Lock size={13} className="text-ws-ink-4 shrink-0" />;
    default:
      return <Circle size={13} className="text-ws-ink-4 shrink-0" />;
  }
}

// Compact "n/total" pill shown on a week folder that carries lessons.
function WeekProgressPill({ week }: { week: Week }) {
  const { completed, total } = weekLessonProgress(week);
  if (total === 0) return null;
  const done = completed === total;
  return (
    <span
      className={`ml-auto shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
        done ? 'bg-ws-success/15 text-ws-success' : 'bg-ws-surface-2 text-ws-ink-3'
      }`}
      title={`${completed} of ${total} lessons completed`}
    >
      {done ? '✓' : `${completed}/${total}`}
    </span>
  );
}

// Muted, non-clickable lines for a plan-only week's sessions (Review / Test /
// Reading) plus its due items — these weeks carry no lessons to link to.
function SessionLines({ week }: { week: Week }) {
  const sessions = week.schedule?.sessions ?? [];
  const due = week.schedule?.due ?? [];
  if (sessions.length === 0 && due.length === 0) {
    return (
      <div className="pl-9 pr-2.5 py-1 text-xs italic text-ws-ink-4">No sessions yet</div>
    );
  }
  return (
    <div className="mt-0.5">
      {sessions.map((s, i) => (
        <div
          key={i}
          title={s.label}
          className="flex items-center gap-2 pl-9 pr-2.5 py-1.5 text-[13px] text-ws-ink-3"
        >
          <Circle size={6} className="shrink-0 fill-ws-ink-4 text-ws-ink-4" />
          <span className="truncate">{s.label}</span>
        </div>
      ))}
      {due.map((d, i) => (
        <div
          key={`due-${i}`}
          title={`Due: ${d}`}
          className="flex items-center gap-2 pl-9 pr-2.5 py-0.5 text-[11px] text-ws-warning"
        >
          <span className="truncate">⚑ {d}</span>
        </div>
      ))}
    </div>
  );
}

export default function LeftNav({ onNavigate }: LeftNavProps) {
  const courseId = useCourseStore((s) => s.currentCourseId);
  // Admins / super-admins may open any lesson, including locked ones.
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [data, setData] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which week "folders" are expanded. Keyed by week id.
  const [openWeeks, setOpenWeeks] = useState<Record<string, boolean>>({});
  // Not read reactively — only via useUiStore.getState() below and the setter
  // here — so toggling it never forces this whole tree to re-render.
  const setWeekTreeCollapsed = useUiStore((s) => s.setWeekTreeCollapsed);

  useEffect(() => {
    if (!courseId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCourse(courseId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Default every week to open, UNLESS the learner previously left the
        // "Collapse all weeks" preference on — read directly off the store
        // rather than the `weekTreeCollapsed` closure variable so toggling
        // the preference later doesn't re-run this and undo a week the
        // learner opened by hand in the meantime.
        const startCollapsed = useUiStore.getState().weekTreeCollapsed;
        setOpenWeeks(Object.fromEntries(d.weeks.map((w) => [w.id, !startCollapsed])));
      })
      .catch(() => !cancelled && setError('Could not load course'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const weeks = useMemo(() => data?.weeks ?? [], [data]);

  // Course-wide completion for the header progress meter.
  const courseProgress = useMemo(() => {
    let completed = 0;
    let total = 0;
    for (const w of weeks) {
      const p = weekLessonProgress(w);
      completed += p.completed;
      total += p.total;
    }
    return {
      completed,
      total,
      pct: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [weeks]);

  const toggleWeek = (id: string) =>
    setOpenWeeks((prev) => ({ ...prev, [id]: !prev[id] }));

  // Collapse/expand-all: if any week is currently open, collapse them all; if all
  // are collapsed, expand them all. Drives the labelled button in the panel
  // header below, and remembers the choice (weekTreeCollapsed) so the next
  // course loaded — this session or a later one — starts the same way.
  const anyWeekOpen = weeks.some((w) => openWeeks[w.id] ?? true);
  const toggleAllWeeks = () => {
    const nextOpen = !anyWeekOpen;
    setOpenWeeks(Object.fromEntries(weeks.map((w) => [w.id, nextOpen])));
    setWeekTreeCollapsed(!nextOpen);
  };

  return (
    <aside className="flex h-full w-[17.6rem] flex-col border-r border-ws-border bg-ws-surface">
      {/* The course mark and title that used to sit here are gone: TopBar
          already carries the brand and TopNav names the destination, so this
          repeated it a third time and cost vertical space in a panel whose
          job is the week tree. The progress meter stays — that is course
          DATA, not branding, and it has nowhere else to live. */}
      <div className="px-3.5 py-3 border-b border-ws-border">
        {/* Course progress meter */}
        {courseProgress.total > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] font-medium text-ws-ink-3">
              <span>Course progress</span>
              <span className="tabular-nums text-ws-ink-2">{courseProgress.pct}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ws-surface-2">
              <div
                className="ws-progress-fill h-full rounded-full"
                style={{
                  width: `${courseProgress.pct}%`,
                  backgroundImage:
                    'linear-gradient(90deg, var(--ws-accent) 0%, var(--ws-gold) 160%)',
                }}
              />
            </div>
          </div>
        )}

        {/* Collapse / expand every week at once. Lives in this fixed panel
            header — not the scrollable list below — so it is visible without
            scrolling, and carries a real text label rather than a bare icon:
            this used to be an unlabelled size-6 icon tucked above the first
            week, easy to mistake for list chrome rather than a control. It is
            the actual answer to "collapse the left nav panel for weeks";
            TopBar's sidebar toggle (below/adjacent, "Hide sidebar") removes
            the whole panel instead, and the two are named to not be confused
            with each other. Rectangular button, no floating icon. */}
        {courseId && !loading && !error && weeks.length > 0 && (
          <button
            type="button"
            onClick={toggleAllWeeks}
            aria-label={anyWeekOpen ? 'Collapse all weeks' : 'Expand all weeks'}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-ws-border py-1.5 text-xs font-medium text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink-2"
          >
            {anyWeekOpen ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
            {anyWeekOpen ? 'Collapse all weeks' : 'Expand all weeks'}
          </button>
        )}
      </div>

      {/* Named distinctly from TopNav's "Primary" landmark — two unlabeled
          <nav>s read as indistinguishable to a screen-reader's landmark list. */}
      <nav aria-label="Course lessons" className="ws-scroll-thin flex-1 overflow-y-auto px-1.5 py-2">
        {!courseId && (
          <p className="px-2.5 py-6 text-sm text-ws-ink-3">
            Pick a course from the dashboard to see its lessons here.
          </p>
        )}

        {courseId && loading && (
          <div className="px-2.5 py-6">
            <Spinner label="Loading lessons…" />
          </div>
        )}

        {courseId && error && (
          <p className="px-2.5 py-6 text-sm text-ws-danger">{error}</p>
        )}

        {courseId && !loading && !error && weeks.length === 0 && (
          <p className="px-2.5 py-6 text-sm text-ws-ink-3">No content yet.</p>
        )}

        {weeks.map((week) => {
          const isOpen = openWeeks[week.id] ?? true;
          const hasLessons = weekHasLessons(week);
          return (
            <div key={week.id} className="mb-0.5">
              {/* Week folder header */}
              <button
                onClick={() => toggleWeek(week.id)}
                aria-expanded={isOpen}
                className="group flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left text-[13px] font-semibold text-ws-ink-2 hover:bg-ws-surface-2 transition-colors"
              >
                <ChevronRight
                  size={14}
                  className={`shrink-0 text-ws-ink-3 transition-transform duration-200 ${
                    isOpen ? 'rotate-90' : ''
                  }`}
                />
                {isOpen ? (
                  <FolderOpen size={15} className="shrink-0 text-ws-accent" />
                ) : (
                  <Folder size={15} className="shrink-0 text-ws-ink-3" />
                )}
                <span className="truncate">
                  <span className="text-ws-ink-4">W{week.index} · </span>
                  {week.title}
                </span>
                {hasLessons && <WeekProgressPill week={week} />}
              </button>

              {isOpen && (
                <div className="pb-1">
                  {hasLessons ? (
                    week.modules.map((module) => {
                      // Hide the uppercase module label when it merely repeats its
                      // single lesson's title (e.g. module "The Atom" → lesson
                      // "The Atom") — that's pure clutter. Keep it for multi-lesson
                      // modules or when the label actually adds information.
                      const labelRedundant =
                        module.lessons.length === 1 &&
                        module.title.trim().toLowerCase() ===
                          module.lessons[0].title.trim().toLowerCase();
                      return (
                      <div key={module.id} className="mt-0.5">
                        {/* Module group label (omitted when it just repeats the lesson) */}
                        {!labelRedundant && (
                          <div className="px-2.5 pl-9 py-1 text-[10px] font-semibold uppercase tracking-wide text-ws-ink-4 truncate">
                            {module.title}
                          </div>
                        )}
                        {module.lessons.map((lesson) => {
                          // Locked lessons are non-clickable for learners, but
                          // admins/super-admins may open any lesson.
                          const gated = lesson.status === 'locked' && !isAdmin;
                          if (gated) {
                            return (
                              <div
                                key={lesson.id}
                                title="Locked — complete earlier lessons to unlock"
                                aria-disabled="true"
                                className="relative flex cursor-not-allowed items-center gap-2 rounded-lg py-1.5 pl-9 pr-2.5 text-[13px] text-ws-ink-4"
                              >
                                <StatusDot status={lesson.status} />
                                <span className="truncate">{lesson.title}</span>
                              </div>
                            );
                          }
                          return (
                            <NavLink
                              key={lesson.id}
                              to={`/lessons/${lesson.id}`}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                `relative flex items-center gap-2 rounded-lg py-1.5 pl-9 pr-2.5 text-[13px] transition-colors ${
                                  isActive
                                    ? 'bg-ws-accent-quiet text-ws-accent-ink font-medium'
                                    : `${
                                        lesson.status === 'completed'
                                          ? 'text-ws-ink-3'
                                          : 'text-ws-ink-2'
                                      } hover:bg-ws-surface-2`
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  {/* Active accent rail */}
                                  {isActive && (
                                    <span className="absolute left-1.5 top-1.5 bottom-1.5 w-0.5 rounded-full bg-ws-accent" />
                                  )}
                                  <StatusDot status={lesson.status} />
                                  <span
                                    className={`truncate ${
                                      lesson.status === 'in_progress' && !isActive
                                        ? 'font-medium text-ws-ink'
                                        : ''
                                    }`}
                                  >
                                    {lesson.title}
                                  </span>
                                </>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                      );
                    })
                  ) : (
                    // Plan-only weeks (review / test / reading): the schedule
                    // sessions as muted, non-clickable lines.
                    <SessionLines week={week} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* The footer that used to live here — eight destinations squeezed into
          one flex-1 icon row, labels only on hover — is gone. Every one of
          those destinations now lives in components/layout/TopNav.tsx, named,
          in the chrome row above (the instructor's own complaint: "cramped up
          … move them to the top bar so I can see them all the time without
          being lost"). Nothing here replaces it — this aside is the course
          week tree and only the course week tree. */}
    </aside>
  );
}
